/*
 * CHI-20 buyer card 6-digit code, on the EXISTING email-OTP login (server/routers/auth.ts):
 *   auth.requestOtp({ email })            -> { accepted:false, reason:"no_account" } | accepted (+ localPrefillCode in local dev)
 *   auth.verifyOtp({ email, code, returnTo }) -> { requiresTotp:false } | { requiresTotp:true, challengeId }
 *   auth.totp.verifyLogin({ challengeId, code }) -> signed in (existing TOTP continuation)
 * A new email takes the EXISTING CreateAccount path (/create-account?returnTo=…), the same
 * route EventDetail uses. No new endpoints, no forked auth logic; codes arrive via
 * localOtpPrefill / the dev email redirect locally (no real sends).
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { OTP_SIGNS_IN_EXISTING_ACCOUNT } from "@/lib/ticketingApi";

export type OtpPhase =
  | "idle" | "sending" | "sent" | "verifying" | "totp" | "verified"
  | "wrong" | "expired" | "rate_limited" | "send_failed"
  | "guest_sent" | "guest_verifying" | "guest_verified";

export type GuestBuyerProfile = { name: string; email: string; companyName: string; jobTitle: string; phone: string };

function errorCode(error: unknown) {
  return (error as { data?: { code?: string } } | null)?.data?.code;
}

export function useBuyerOtp(opts: { returnTo: string; onSignedIn?: () => void }) {
  const utils = trpc.useUtils();
  const requestOtp = trpc.auth.requestOtp.useMutation();
  const verifyOtp = trpc.auth.verifyOtp.useMutation();
  const verifyTotp = trpc.auth.totp.verifyLogin.useMutation();
  // The ticket guest procedures are shipped by the ticketing router alongside this UI.
  // Keep this boundary permissive while API types are regenerated with the server change.
  const events: any = trpc.events;
  const startGuest = events.ticketGuestStart.useMutation();
  const verifyGuest = events.ticketGuestVerify.useMutation();
  const [phase, setPhase] = useState<OtpPhase>("idle");
  const [prefill, setPrefill] = useState<string | undefined>(undefined);
  const [challengeId, setChallengeId] = useState<string | null>(null);

  const signedIn = async () => {
    setPhase("verified");
    try { await utils.auth.me.invalidate(); } catch { /* session cookie is already set */ }
    opts.onSignedIn?.();
  };

  async function send(email: string) {
    setPhase("sending");
    try {
      const res: any = await requestOtp.mutateAsync({ email: email.trim() });
      if (res && res.accepted === false && res.reason === "no_account") {
        setPhase("send_failed");
        return;
      }
      if (!OTP_SIGNS_IN_EXISTING_ACCOUNT) {
        setPhase("send_failed");
        return;
      }
      setPrefill(res?.localPrefillCode ?? undefined);
      setPhase("sent");
    } catch (error) {
      setPhase(errorCode(error) === "TOO_MANY_REQUESTS" ? "rate_limited" : "send_failed");
    }
  }

  async function sendGuest(profile: GuestBuyerProfile) {
    setPhase("sending");
    try {
      await startGuest.mutateAsync({
        email: profile.email.trim().toLowerCase(),
        name: profile.name.trim(),
        companyName: profile.companyName.trim(),
        jobTitle: profile.jobTitle.trim(),
        phone: profile.phone,
      });
      setPrefill(undefined);
      setPhase("guest_sent");
    } catch (error) {
      setPhase(errorCode(error) === "TOO_MANY_REQUESTS" ? "rate_limited" : "send_failed");
    }
  }

  async function verifyGuestCode(profile: GuestBuyerProfile, code: string) {
    setPhase("guest_verifying");
    try {
      await verifyGuest.mutateAsync({
        email: profile.email.trim().toLowerCase(),
        name: profile.name.trim(),
        companyName: profile.companyName.trim(),
        jobTitle: profile.jobTitle.trim(),
        phone: profile.phone,
        code,
      });
      setPhase("guest_verified");
    } catch (error) {
      setPhase(errorCode(error) === "TOO_MANY_REQUESTS" ? "rate_limited" : "wrong");
    }
  }

  async function verify(email: string, code: string) {
    setPhase("verifying");
    try {
      const res: any = await verifyOtp.mutateAsync({ email: email.trim(), code, returnTo: opts.returnTo, trustDevice: false });
      if (res?.requiresTotp) {
        setChallengeId(res.challengeId);
        setPhase("totp");
        return;
      }
      await signedIn();
    } catch (error) {
      setPhase(errorCode(error) === "TOO_MANY_REQUESTS" ? "rate_limited" : "wrong");
    }
  }

  async function verifyAuthenticator(code: string) {
    if (!challengeId) return;
    setPhase("verifying");
    try {
      await verifyTotp.mutateAsync({ challengeId, code });
      await signedIn();
    } catch {
      setPhase("totp");
    }
  }

  return {
    phase,
    prefill,
    send,
    sendGuest,
    verifyGuestCode,
    markGuestVerified: () => setPhase("guest_verified"),
    verify,
    verifyAuthenticator,
    reset: () => { setPhase("idle"); setChallengeId(null); setPrefill(undefined); },
  };
}
